import { Chip, Typography, Box, Card, Stack } from "@mui/material";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import RevenueStreamsTable from "./RevenueStreamsTable";
import type { RevenueRow } from "./RevenueStreamsTable";

interface TargetVsActualItem {
  Proportional_Target: number;
  Actual: number;
  Target: number;
  Comment: string;
  Metrics: string;
  RR: number;
  Proportional_Attainment: number;
}

interface RevenueStreamsProps {
  data: TargetVsActualItem[];
}

const TotalIncomeTableHeadings = {
  metric: "",
  actual_target: "Actual / Target",
  RR: "RR",
  PRR: "PRR",
};

function fmt(value: number): string {
  const rounded = Math.round(value);
  return `$${rounded.toLocaleString()}`;
}

function getRrColor(attainment: number): "success" | "warning" | "error" {
  if (attainment > 0.99) return "success"; // Green → > 99%
  if (attainment >= 0.85) return "warning"; // Yellow → 85% to 99%
  return "error"; // Red → < 85%
}

function getRrChipStyles(attainment: number) {
  const color = getRrColor(attainment);
  const map = {
    success: {
      bg: colors.Success[50].$value,
      color: colors.Success[600].$value,
    },
    warning: {
      bg: colors.Warning[50].$value,
      color: colors.Warning[700].$value,
    },
    error: {
      bg: colors.Error[50].$value,
      color: colors.Error[600].$value,
    },
  };
  return map[color];
}

export default function RevenueStreams({ data }: RevenueStreamsProps) {
  const findMetric = (name: string) =>
    data?.find((item) => item.Metrics?.toLowerCase() === name.toLowerCase());

  const totalIncome = findMetric("TOTAL_Income");
  const cpaIncome = findMetric("CPA_Income");
  const rsIncome = findMetric("RS_Income");

  // Build table rows from API data
  const tableRows: RevenueRow[] = [];
  if (cpaIncome) {
    tableRows.push({
      cron_id: "1",
      rrType: getRrColor(cpaIncome.Proportional_Attainment),
      metric: "CPA",
      actual_target: `${fmt(cpaIncome.Actual)} / ${fmt(cpaIncome.Target)}`,
      RR: `${(cpaIncome.Proportional_Attainment * 100).toFixed(1)}%`,
      PRR: fmt(cpaIncome.RR),
    });
  }
  if (rsIncome) {
    tableRows.push({
      cron_id: "2",
      rrType: getRrColor(rsIncome.Proportional_Attainment),
      metric: "RS",
      actual_target: `${fmt(rsIncome.Actual)} / ${fmt(rsIncome.Target)}`,
      RR: `${(rsIncome.Proportional_Attainment * 100).toFixed(1)}%`,
      PRR: fmt(rsIncome.RR),
    });
  }

  return (
    <Card
      sx={{
        p: spacing.Spacing.md.$value,
        borderRadius: spacing.Radius.xl.$value,
        boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
        border: "1px solid" + colors.GrayLightMode[200].$value,
      }}
    >
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        mb={2}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: typography.Font_weight.Medium.$value,
              color: colors.GrayLightMode[600].$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
            }}
          >
            TOTAL INCOME
          </Typography>

          <Typography
            sx={{
              fontSize: typography.Size.Text.xl.$value,
              fontWeight: 600,
              color: colors.GrayLightMode[900].$value,
              mt: spacing.Spacing.xs.$value,
            }}
          >
            {totalIncome ? fmt(totalIncome.Actual) : "N/A"}
          </Typography>

          <Typography
            sx={{
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              color: colors.GrayLightMode[600].$value,
              mt: spacing.Spacing.xxs.$value,
            }}
          >
            out of{" "}
            <Box
              component="span"
              fontWeight={600}
              color={colors.GrayLightMode[900].$value}
            >
              {totalIncome ? fmt(totalIncome.Target) : "N/A"}
            </Box>
          </Typography>
        </Box>

        <Stack spacing={spacing.Spacing.sm.$value} alignItems="flex-end">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              color={colors.GrayLightMode[700].$value}
              sx={{
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
              }}
            >
              RR
            </Typography>
            <Chip
              label={
                totalIncome
                  ? `${(totalIncome.Proportional_Attainment * 100).toFixed(1)}%`
                  : "N/A"
              }
              size="small"
              sx={{
                bgcolor: totalIncome
                  ? getRrChipStyles(totalIncome.Proportional_Attainment).bg
                  : undefined,
                color: totalIncome
                  ? getRrChipStyles(totalIncome.Proportional_Attainment).color
                  : undefined,
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
                borderRadius: spacing.Radius.sm.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                "& .MuiChip-label": {
                  px: spacing.Spacing.md.$value,
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              color={colors.GrayLightMode[600].$value}
              sx={{
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
              }}
            >
              PRR
            </Typography>
            <Typography
              color={colors.GrayLightMode[900].$value}
              sx={{
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            >
              {totalIncome ? fmt(totalIncome.RR) : "N/A"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* TABLE */}
      <RevenueStreamsTable
        TotalIncomeTableHeadings={TotalIncomeTableHeadings}
        TotalIncomeTableRows={tableRows}
      />

      {/* ── Original static data (kept for reference) ──────────────── */}
      {/* 
      Header values:
        TOTAL INCOME: $1,093,140 out of $1,478,063, RR 105.65%, PRR $1,561,629

      Table rows:
        CPA: $1,614,441 / $1,239,322, RR $375,119, PRR 130.3%
        RS:  $394 / $399, RR -$5, PRR 98.6%
      */}
    </Card>
  );
}
